import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { z } from 'zod'

import { documentGraphQueryOptions } from '#/api/candidates'
import type { CandidatesMode } from '#/components/candidate-graph/candidate-explorer'
import { CandidatesBody } from '#/components/candidate-graph/candidates-body'
import { DocumentCandidates } from '#/components/candidate-graph/document-candidates'
import { useGraphViewedEvent } from '#/components/candidate-graph/use-graph-viewed-event'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import { hasPermission } from '#/user/me'

const candidatesSearchSchema = z.object({
  node: z.string().optional(),
  mode: z.enum(['overview', 'focus']).optional(),
})

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/workspaces/$workspaceId/documents/$fileUploadId/candidates',
)({
  validateSearch: candidatesSearchSchema,
  beforeLoad: ({ context, params }) => {
    if (!hasPermission(context.me, 'annwn:internal:read')) {
      throw redirect({
        to: '/accounts/$accountId/workspaces/$workspaceId/documents',
        params: {
          accountId: params.accountId,
          workspaceId: params.workspaceId,
        },
        replace: true,
      })
    }
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      documentGraphQueryOptions(
        params.accountId,
        params.workspaceId,
        params.fileUploadId,
      ),
    ),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: CandidatesView,
})

function CandidatesView() {
  const { t } = useTranslation()
  const { accountId, workspaceId, fileUploadId } = Route.useParams()
  const { node, mode: modeParam } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data } = useSuspenseQuery(
    documentGraphQueryOptions(accountId, workspaceId, fileUploadId),
  )

  // The overview is the front door: "what came out of this document" is the
  // question you arrive with, and a specific node is the question you arrive
  // with only when the link already names one. A `node` deep link therefore
  // implies focus even without an explicit `mode`, since showing the overview
  // and leaving that node unused would defeat the link.
  const mode: CandidatesMode =
    modeParam ?? (node !== undefined ? 'focus' : 'overview')

  useGraphViewedEvent({
    accountId,
    workspaceId,
    fileUploadId,
    data,
    landingMode: mode,
  })

  const changeMode = (next: CandidatesMode) => {
    void navigate({ search: (prev) => ({ ...prev, mode: next }) })
  }

  const focusNode = (uuid: string) => {
    // Re-selecting the node already in focus, while already in focus mode,
    // would push a history entry identical to the current one, so back would
    // appear not to work. Arriving from the overview always proceeds, even
    // onto a uuid left over from an earlier focus session, because the mode
    // switch itself is the point.
    if (uuid === node && mode === 'focus') return
    // A push, not a replace: each hop becomes a history entry so browser-back
    // walks the trail.
    void navigate({
      search: (prev) => ({ ...prev, node: uuid, mode: 'focus' }),
    })
  }

  return (
    <DocumentCandidates
      fileName={data.meta.file_upload_filename}
      data={data}
      leading={
        <Link
          to="/accounts/$accountId/workspaces/$workspaceId/documents"
          params={{ accountId, workspaceId }}
          className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          {t('candidateGraph.header.backToDocuments')}
        </Link>
      }
    >
      <CandidatesBody
        data={data}
        mode={mode}
        onModeChange={changeMode}
        focusedUuid={node}
        onFocusNode={focusNode}
      />
    </DocumentCandidates>
  )
}
